// sapi-admin/src/pages/AdminProfile.jsx
import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { 
  User, Mail, Phone, Calendar, Shield, 
  Smartphone, Edit2, Save, X, 
  Bell, ShoppingBag, Users, TrendingUp, Key,
  Volume2, VolumeX
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useSound } from '../contexts/SoundContext';
import { useNotificationSound } from '../hooks/useNotificationSound';

// Composants UI
const GlassPanel = ({ children, style = {} }) => (
  <div style={{ background: "rgba(17,24,39,.85)", border: "1px solid rgba(255,255,255,.06)", borderRadius: 16, backdropFilter: "blur(16px)", ...style }}>
    {children}
  </div>
);

const Btn = ({ children, onClick, variant = 'ghost', size = 'sm', disabled = false, style = {} }) => {
  const V = { 
    ghost: { background: 'rgba(255,255,255,.06)', color: '#cbd5e1', border: '1px solid rgba(255,255,255,.08)' },
    blue: { background: 'rgba(59,130,246,.15)', color: '#60a5fa', border: '1px solid rgba(59,130,246,.3)' },
    green: { background: 'rgba(16,185,129,.15)', color: '#34d399', border: '1px solid rgba(16,185,129,.3)' },
    red: { background: 'rgba(239,68,68,.15)', color: '#f87171', border: '1px solid rgba(239,68,68,.3)' },
    amber: { background: 'rgba(245,158,11,.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,.3)' },
  };
  const v = V[variant] || V.ghost;
  const pad = size === 'xs' ? '3px 8px' : size === 'sm' ? '5px 12px' : '8px 18px';
  return <button onClick={onClick} disabled={disabled} style={{ ...v, padding: pad, borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? .45 : 1, display: 'inline-flex', alignItems: 'center', gap: 5, transition: 'opacity .15s', whiteSpace: 'nowrap', ...style }}>{children}</button>;
};

const Field = ({ label, value, icon: Icon, type = 'text', onChange, disabled = true }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.12em', color: '#475569', display: 'block', marginBottom: 6 }}>
      {Icon && <Icon size={12} style={{ marginRight: 4, display: 'inline' }} />} {label}
    </label>
    <input
      type={type}
      value={value || ''}
      onChange={onChange}
      disabled={disabled}
      style={{ 
        width: '100%', 
        background: disabled ? 'rgba(255,255,255,.03)' : 'rgba(255,255,255,.06)',
        border: `1px solid ${disabled ? 'rgba(255,255,255,.05)' : 'rgba(59,130,246,.3)'}`,
        borderRadius: 10, 
        padding: '10px 14px', 
        color: disabled ? '#64748b' : '#e2e8f0',
        fontSize: 13,
        outline: 'none',
        transition: 'all .15s'
      }}
    />
  </div>
);

const StatCard = ({ label, value, icon: Icon, accent = "#3b82f6" }) => (
  <div style={{ background: 'rgba(255,255,255,.03)', border: `1px solid ${accent}33`, borderRadius: 12, padding: '12px 14px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <Icon size={14} color={accent} />
      <span style={{ fontSize: 10, color: '#475569' }}>{label}</span>
    </div>
    <p style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>{value}</p>
  </div>
);

// ID de l'administrateur
const ADMIN_USER_ID = '3Kpi6gIXcNVmmUJsRmhui4qN6af1';

export default function AdminProfile() {
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'Super Admin',
    bio: '',
    avatar: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalUsers: 0,
    totalRevenue: 0,
    notificationsEnabled: true
  });

  // ✅ Utiliser le contexte sonore
  const { soundEnabled, toggleSound } = useSound();
  
  // ✅ Utiliser le hook de notification sonore
  const { testSound, userInteracted } = useNotificationSound(soundEnabled);

  useEffect(() => {
    const loadAdmin = async () => {
      setLoading(true);
      try {
        const adminDoc = await getDoc(doc(db, 'users', ADMIN_USER_ID));
        if (adminDoc.exists()) {
          const data = adminDoc.data();
          setProfile(prev => ({
            ...prev,
            name: data.name || 'Administrator',
            email: data.email || 'admin@sapi.etralis.com',
            phone: data.phone || '',
            bio: data.bio || '',
            avatar: data.profilePic || '',
            role: data.userRole || 'Super Admin'
          }));
        }
        
        const ordersSnap = await getDocs(collection(db, 'orders'));
        const usersSnap = await getDocs(collection(db, 'users'));
        let totalRevenue = 0;
        ordersSnap.docs.forEach(doc => {
          totalRevenue += doc.data().totalAmount || 0;
        });
        
        setStats({
          totalOrders: ordersSnap.size,
          totalUsers: usersSnap.size,
          totalRevenue: totalRevenue.toFixed(2),
          notificationsEnabled: true
        });
        
      } catch (error) {
        console.error('Error loading profile:', error);
        toast.error('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    
    loadAdmin();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', ADMIN_USER_ID), {
        name: profile.name,
        phone: profile.phone,
        bio: profile.bio,
        updatedAt: new Date().toISOString()
      });
      setIsEditing(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div style={{ width: 44, height: 44, border: '3px solid rgba(59,130,246,.3)', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ paddingBottom: 48 }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
          <div>
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.18em', color: 'rgba(59,130,246,.8)', marginBottom: 4 }}>Account</p>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: 10 }}>
              <User size={20} color="#3b82f6" strokeWidth={2} /> Administrator Profile
            </h1>
            <p style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>Manage your admin account and preferences</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {!isEditing ? (
              <Btn variant="blue" onClick={() => setIsEditing(true)}>
                <Edit2 size={13} /> Edit Profile
              </Btn>
            ) : (
              <>
                <Btn variant="ghost" onClick={() => setIsEditing(false)}>
                  <X size={13} /> Cancel
                </Btn>
                <Btn variant="green" onClick={handleSave} disabled={saving}>
                  <Save size={13} /> {saving ? 'Saving...' : 'Save Changes'}
                </Btn>
              </>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 20 }}>
        
        {/* Left Column - Profile Info */}
        <GlassPanel>
          <div style={{ padding: 20, textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,.06)' }}>
            <div style={{ 
              width: 80, height: 80, borderRadius: '50%', 
              background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 12px'
            }}>
              <span style={{ fontSize: 28, fontWeight: 700, color: '#fff' }}>
                {profile.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>{profile.name}</h2>
            <p style={{ fontSize: 11, color: '#60a5fa', marginTop: 2 }}>{profile.role}</p>
            <p style={{ fontSize: 10, color: '#475569', marginTop: 4 }}>System Administrator</p>
          </div>
          
          <div style={{ padding: 20 }}>
            <Field label="Full Name" value={profile.name} icon={User} onChange={e => setProfile({...profile, name: e.target.value})} disabled={!isEditing} />
            <Field label="Email Address" value={profile.email} icon={Mail} disabled={true} />
            <Field label="Phone Number" value={profile.phone} icon={Phone} onChange={e => setProfile({...profile, phone: e.target.value})} disabled={!isEditing} />
            <Field label="Bio / Description" value={profile.bio} icon={User} onChange={e => setProfile({...profile, bio: e.target.value})} disabled={!isEditing} />
          </div>
        </GlassPanel>

        {/* Right Column - Stats & Preferences */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          
          {/* Stats Cards */}
          <GlassPanel>
            <div style={{ padding: 16, borderBottom: '1px solid rgba(255,255,255,.06)' }}>
              <h3 style={{ fontSize: 13, fontWeight: 600, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Shield size={14} color="#3b82f6" /> Admin Statistics
              </h3>
            </div>
            <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              <StatCard label="Total Orders" value={stats.totalOrders} icon={ShoppingBag} accent="#10b981" />
              <StatCard label="Total Users" value={stats.totalUsers} icon={Users} accent="#8b5cf6" />
              <StatCard label="Total Revenue" value={`${stats.totalRevenue} π`} icon={TrendingUp} accent="#f59e0b" />
              <StatCard label="Admin Since" value="2024" icon={Calendar} accent="#3b82f6" />
            </div>
          </GlassPanel>

          {/* Preferences - avec toggle sonore */}
          <GlassPanel>
            <div style={{ padding: 16, borderBottom: '1px solid rgba(255,255,255,.06)' }}>
              <h3 style={{ fontSize: 13, fontWeight: 600, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Bell size={14} color="#3b82f6" /> Preferences
              </h3>
            </div>
            <div style={{ padding: 16 }}>
              {/* ✅ Toggle pour le son des notifications */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 500, color: '#e2e8f0' }}>Notification Sound</p>
                  <p style={{ fontSize: 10, color: '#475569' }}>Play sound when new notifications arrive</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button
                    onClick={() => {
                      if (!userInteracted) {
                        toast.warning('Click anywhere on the page first to enable sound');
                      } else {
                        testSound();
                        toast.info('🔊 Test sound played');
                      }
                    }}
                    style={{ 
                      padding: '6px 10px', 
                      borderRadius: 8, 
                      background: 'rgba(59,130,246,.15)', 
                      border: '1px solid rgba(59,130,246,.3)',
                      color: '#60a5fa',
                      fontSize: 10,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}
                  >
                    {soundEnabled ? <Volume2 size={12} /> : <VolumeX size={12} />}
                    Test
                  </button>
                  <button
                    onClick={toggleSound}
                    style={{ 
                      width: 44, height: 24, borderRadius: 12, 
                      background: soundEnabled ? '#3b82f6' : 'rgba(255,255,255,.2)',
                      position: 'relative',
                      cursor: 'pointer',
                      border: 'none'
                    }}
                  >
                    <div style={{
                      width: 20, height: 20, borderRadius: '50%', background: '#fff',
                      position: 'absolute', top: 2,
                      left: soundEnabled ? 22 : 2,
                      transition: 'left .2s'
                    }} />
                  </button>
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 500, color: '#e2e8f0' }}>Dark Mode</p>
                  <p style={{ fontSize: 10, color: '#475569' }}>Always enabled for admin panel</p>
                </div>
                <div style={{ width: 40, height: 22, borderRadius: 11, background: '#3b82f6', position: 'relative' }}>
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, right: 2 }} />
                </div>
              </div>
            </div>
          </GlassPanel>

          {/* Session & Security Info */}
          <GlassPanel>
            <div style={{ padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <Smartphone size={16} color="#475569" />
                <span style={{ fontSize: 11, color: '#475569' }}>Security Information</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 10, color: '#334155' }}>Admin ID</span>
                <span style={{ fontSize: 10, color: '#475569', fontFamily: 'monospace' }}>{ADMIN_USER_ID.slice(0, 16)}...</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 10, color: '#334155' }}>Access Level</span>
                <span style={{ fontSize: 10, color: '#34d399', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Key size={10} /> Full Access
                </span>
              </div>
            </div>
          </GlassPanel>
        </div>
      </div>
    </motion.div>
  );
}